<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import V4Button from 'next/button/Button.vue';
import Spinner from 'next/spinner/Spinner.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import CustomDashboardForm from '../components/CustomDashboardForm.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const uiFlags = useMapGetter('wgptCustomDashboards/getUIFlags');
const activeDashboard = useMapGetter('wgptCustomDashboards/getActiveDashboard');

const dashboardId = computed(() => route.params.dashboardId);
const isSubmitting = computed(() => uiFlags.value.isUpdating);
const isLoading = computed(() => uiFlags.value.isFetchingItem);
const isReady = ref(false);
const formRef = ref(null);

async function handleSubmit(payload) {
  try {
    await store.dispatch('wgptCustomDashboards/update', {
      id: dashboardId.value,
      ...payload,
    });
    useAlert(t('WGPT_CUSTOM_DASHBOARDS.FORM.UPDATE_SUCCESS'));
    router.push(
      accountScopedRoute('wgpt_reports_custom_dashboard_show', {
        dashboardId: dashboardId.value,
      })
    );
  } catch (error) {
    useAlert(error?.message || t('WGPT_CUSTOM_DASHBOARDS.FORM.UPDATE_ERROR'));
  }
}

function handleCancel() {
  router.push(accountScopedRoute('wgpt_settings_custom_dashboards_list'));
}

onMounted(async () => {
  try {
    await store.dispatch('wgptCustomDashboards/show', dashboardId.value);
    isReady.value = true;
  } catch (error) {
    useAlert(error?.message || t('WGPT_CUSTOM_DASHBOARDS.VIEWER.ERROR'));
    router.replace(accountScopedRoute('wgpt_settings_custom_dashboards_list'));
  }
});
</script>

<template>
  <section class="flex flex-col h-full w-full overflow-hidden bg-n-surface-1">
    <div class="border-b border-solid border-n-weak/60 shrink-0">
      <div class="max-w-7xl w-full mx-auto pt-4 pb-4 px-6">
        <BackButton compact />
        <div class="flex items-center justify-between gap-5 mt-2">
          <h1 class="text-2xl text-n-slate-12 font-medium mb-0 truncate">
            {{ activeDashboard.name || t('WGPT_CUSTOM_DASHBOARDS.EDIT_TITLE') }}
          </h1>
          <div class="flex items-center gap-3 shrink-0">
            <V4Button
              type="button"
              variant="ghost"
              :label="t('WGPT_CUSTOM_DASHBOARDS.FORM.CANCEL')"
              @click="handleCancel"
            />
            <V4Button
              type="button"
              :label="t('WGPT_CUSTOM_DASHBOARDS.FORM.SAVE')"
              :is-loading="isSubmitting"
              :disabled="!formRef?.canSubmit || isSubmitting"
              @click="formRef?.submit()"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-auto px-6 py-6">
      <div class="max-w-7xl mx-auto pb-12">
        <div v-if="isLoading || !isReady" class="flex justify-center py-16">
          <Spinner />
        </div>
        <CustomDashboardForm
          v-else
          ref="formRef"
          :initial-dashboard="activeDashboard"
          @submit="handleSubmit"
        />
      </div>
    </div>
  </section>
</template>
